import React from 'react';
import { TFunction } from 'i18next';
import _get from 'lodash/get';
import { Counter } from 'graphql-common';
import Input from '@lib/components/Input/Input';
import { FieldItem } from '@lib/interfaces/form';
import FieldNames from '@lib/enums/fieldNames/assetCounterIndexFieldNames';
import AssetCounterInfoView from 'components/AssetCounterInfoView/AssetCounterInfoView';
import RadioButtonGroup, {
  RadioButtonWrapViews,
} from '@lib/components/RadioButtonGroup/RadioButtonGroup';
import {
  getDefaultNumberValidation,
  getOptionValidation,
} from '@lib/utils/validationUtils';
import RadioButtonAsButton from '@lib/components/RadioButtonAsButton/RadioButtonAsButton';
import { getMeasurementAbbreviation } from '@lib/utils/measure';
import { CounterIndexAction } from 'components/AssetCounterIndexModalForm/enums';
import { DECIMAL_NUMBER_INPUT_PROPS } from '@lib/enums/form';

type Args = {
  assetCounter: Counter;
  t: TFunction<'translation', undefined>;
};

export default function getFormFields({ assetCounter, t }: Args): FieldItem[] {
  const indexAbbreviation = getMeasurementAbbreviation(
    assetCounter?.measureType,
    assetCounter?.measureUnit,
    t,
  );
  return [
    {
      name: FieldNames.CounterView,
      customRender: () => <AssetCounterInfoView counter={assetCounter} />,
    },
    {
      name: FieldNames.Action,
      element: RadioButtonGroup,
      validation: getOptionValidation({ t, required: true }),
      componentProps: {
        options: [
          { label: t('enter-index'), value: CounterIndexAction.Index },
          { label: t('reset-to-zero'), value: CounterIndexAction.Zero },
        ],
        RadioButtonComponent: RadioButtonAsButton,
        wrapView: RadioButtonWrapViews.row,
      },
      fieldChangeCallBack: ({ values, setValue, name }) => {
        if (name === FieldNames.Action) {
          if (
            _get(values, [FieldNames.Action, 'value']) ===
            CounterIndexAction.Zero
          ) {
            setValue(FieldNames.Index, '0');
          } else {
            setValue(FieldNames.Index, '');
          }
        }
      },
    },
    {
      name: FieldNames.Index,
      label: indexAbbreviation
        ? `${t('index')} ${indexAbbreviation}`
        : t('index'),
      element: Input,
      componentProps: {
        ...DECIMAL_NUMBER_INPUT_PROPS,
        rightIcon: assetCounter?.prohibitLowerIndexThanPrevious
          ? 'info'
          : undefined,
        rightIconSymbolsOutlined: true,
        rightIconTooltipProps: assetCounter?.prohibitLowerIndexThanPrevious
          ? {
              body: t('index-validation-desc'),
            }
          : undefined,
      },
      validation: getDefaultNumberValidation({ t, required: true }),
      isDisabledFn: (values) =>
        _get(values, [FieldNames.Action, 'value']) === 'zero',
    },
  ];
}
